import { GetTemplateFn } from '@mfec/lit/mixins/TemplatingMixin';
import { LitElement, nothing } from 'lit';
import { property, queryAll, state } from 'lit/decorators.js';

import { CustomerAttribute } from './types';

// Allows:
// cattr:product
// cattr:product:item1,item2
export const customerAttributeModeMatcher = /cattr:(?<customerAttributeId>\w+)(:(?<editableItemIds>[\w,]+))?$/;

//
// Base class to extend from in the microfrontends for displaying
// a customer attribute INLINE without a modal dialog
//
export class TefComsCustomerAttributeInlineBase extends LitElement {
  @property({ type: String }) mode: string;

  @state() customerAttributeItemsStatusChanged: boolean = false;
  @queryAll('input[data-cattr-item-id]') items$: NodeListOf<HTMLInputElement>;

  customerAttributeOrigin: CustomerAttribute | undefined = undefined;
  id: string;
  editableItemIds: string[] = [];

  // -------------- START: MUST BE OVERWRITTEN ---------------
  // These methods depend on the implementation of the store (redux? mobx? fancynew?)
  //
  get customerAttributes() : CustomerAttribute[] {
    throw new Error(`[${this.constructor.name}] You must implement get customerAttribute()`);
  }

  // This can only be found out AFTER initializing the TemplatingMixin in the extending component
  getTemplateFnFor(templatename: string) : GetTemplateFn {
    throw new Error(`[${this.constructor.name}] You must implement getTemplateFnFor(${templatename})`);
  }

  get theme() : string {
    throw new Error(`[${this.constructor.name}] You must implement get theme()`);
  }

  cancel() {
    throw new Error(`[${this.constructor.name}] You must implement cancel()`);
  }

  save() {
    throw new Error(`[${this.constructor.name}] You must implement save()`);
  }
  // -------------- END: MUST BE OVERWRITTEN ---------------

  connectedCallback() {
    super.connectedCallback();
    this.setup();
    this.customerAttributeOrigin = structuredClone(this.customerAttributes.find((cattr) => cattr.id === this.id));
    this.addEventListener('change', this.handleChangeEvent.bind(this) as EventListenerOrEventListenerObject);
  }

  setup() {
    const modeMatch = RegExp(customerAttributeModeMatcher).exec(this.mode);
    if (!modeMatch) {
      throw new Error(`[${this.constructor.name}] Invalid mode='${this.mode}'`);
    }
    this.id = modeMatch.groups!.customerAttributeId;
    if (modeMatch.groups!.editableItemIds) {
      this.editableItemIds = modeMatch.groups!.editableItemIds.split(',').map((item) => item.trim());
    }
  }

  handleChangeEvent(e: Event) {
    const target = e.target as HTMLInputElement;

    // We only care for the <input>s of the customer attribute to detect changes
    if (target.dataset.cattrItemId && this.customerAttributeOrigin) {
      this.customerAttributeItemsStatusChanged = false;
      const groupItem = this.customerAttributeOrigin.extra.items.find((item) => item.id === target.dataset.cattrItemId);
      if (groupItem && groupItem.granted !== target.checked) {
        this.customerAttributeItemsStatusChanged = true;
      }
    }
  }

  // eslint-disable-next-line sonarjs/function-return-type
  render() {
    if (!this.hasAnyEditableCustomerAttributeItems) {
      return nothing;
    }

    const inlineDisplayFn = this.getTemplateFnFor('tefComsCustomerAttributeInline');
    return [inlineDisplayFn(this.customerAttribute)];
  }

  isReadonlyCustomerAttributeItem(itemId: string) {
    const groupItem = this.customerAttributeOrigin!.extra.items.find((item) => item.id === itemId);
    if (groupItem && (groupItem?.granted || (this.editableItemIds.length > 0 && !this.editableItemIds.includes(groupItem.id)))) {
      return true;
    }
    return false;
  }

  // Returns the full customer attribute with updated "granted" values for each extra.item, based on the DOM <input> checked state
  get updatedCustomerAttribute() {
    const customerAttribute = structuredClone(this.customerAttribute);
    this.items$.forEach((item$) => {
      const item = customerAttribute.extra.items.find((i) => i.id === item$.dataset.cattrItemId);
      if (item) {
        item.granted = item$.checked;
      }
    });
    return customerAttribute;
  }

  get customerAttribute() {
    const customerAttribute = this.customerAttributes.find((cattr) => cattr.id === this.id)!;
    if (customerAttribute) {
      customerAttribute.extra.items.forEach((item) => {
        item.display = !this.isReadonlyCustomerAttributeItem(item.id); // eslint-disable-line no-param-reassign
      });
    }
    return customerAttribute;
  }

  get hasAnyEditableCustomerAttributeItems() {
    return this.customerAttribute?.extra.items.some((item) => !this.isReadonlyCustomerAttributeItem(item.id));
  }

  // Render to lightDOM
  createRenderRoot() {
    return this;
  }
}
